// src/plugins/sales-forecast/components/CloneNeighborhoodControl.tsx
import React, { useState, useEffect, useMemo } from "react";
import neighborhoodList from "../neighborhoods.json";
import neighborhoodGroupList from "../neighborhood_groups.json";
import marketList from "../markets.json";
import schoolDistrictList from "../school_districts.json";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface CloneEntry {
  id: string;
  title: string;
  cloneType: "Neighborhood" | "Neighborhood - Group" | "Market" | "School District";
  status: string;
  defaults: {
    interestRate: number;
    interestRateThreeMonthChange: number;
    interestRateSixMonthChange: number;
    interestRateNineMonthChange: number;
    interestRateTwelveMonthChange: number;
    interestRateTwoYearChange: number;
    initialSpecs: number;
    preSolds: number;
    specsToStart: number;
    perSpecsSold: number;
    avgPriceMin: number;
    avgPriceMax: number;
    availableLots: number;
    squareFootMin: number;
    squareFootMax: number;
    composition: number;
    amenities: string[];
    threeMonthPriceChange: number;
    sixMonthPriceChange: number;
    nineMonthPriceChange: number;
    twelveMonthPriceChange: number;
    twoYearPriceChange: number;
    bedrooms: number;
    baths: number;
    stories: number;
    yearBuilt: number;
    garageType: string;
    garageSpaces: number;
    city: string;
    lotType: string;
    listingDay: number;
    listingMonth: string;
    activeComps: number;
    cloneDate?: string;
  };
}

interface CloneNeighborhoodControlProps {
  onClone: (entry: CloneEntry) => void;
}

type CloneType = CloneEntry["cloneType"];
type StatusType = "Active" | "Developing" | "Archived";

export const CloneNeighborhoodControl: React.FC<CloneNeighborhoodControlProps> = ({
  onClone,
}) => {
  const theme = useSystemTheme();

  const today = new Date().toISOString().split("T")[0];

  const [cloneType, setCloneType] = useState<CloneType>("Neighborhood");
  const [status, setStatus] = useState<StatusType>("Active"); // ✅ NEW
  const [allEntries, setAllEntries] = useState<CloneEntry[]>([]); // ✅ renamed (unfiltered)
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(today);

  /** Helper: safely map JSON data to CloneEntry[] */
  const buildList = (source: any[], type: CloneType): CloneEntry[] =>
    (source || []).map((n) => ({
      id: n.id,
      title: n.title,
      cloneType: type,
      status: n.status,
      defaults: {
        ...n.defaults,
        amenities: Array.isArray(n?.defaults?.amenities) ? [...n.defaults.amenities] : [],
        cloneDate: selectedDate,
      },
    }));

  /** Human label helper */
  const selectionLabel =
    cloneType === "Neighborhood"
      ? "Neighborhood Selection"
      : cloneType === "Neighborhood - Group"
        ? "Neighborhood Group Selection"
        : cloneType === "Market"
          ? "Market Selection"
          : "School District Selection";

  /** Decide if status filtering applies */
  const statusFilterApplies =
    cloneType === "Neighborhood" || cloneType === "Neighborhood - Group";

  /** Filtered list actually shown in dropdown */
  const entries = useMemo(() => {
    if (!statusFilterApplies) return allEntries;

    // defend against missing/odd values
    return allEntries.filter((e) => (e.status ?? "").toLowerCase() === status.toLowerCase());
  }, [allEntries, status, statusFilterApplies]);

  /** Effect: rebuild entries whenever cloneType changes (not date!) */
  useEffect(() => {
    let sourceList: any[] = [];

    if (cloneType === "Neighborhood") sourceList = neighborhoodList as any[];
    else if (cloneType === "Neighborhood - Group") sourceList = neighborhoodGroupList as any[];
    else if (cloneType === "Market") sourceList = marketList as any[];
    else if (cloneType === "School District") sourceList = schoolDistrictList as any[];

    const list = buildList(sourceList, cloneType);
    setAllEntries(list);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneType]); // ✅ only runs when cloneType changes

  /**
   * Effect: whenever the *visible* entries list changes (cloneType/status),
   * pick a valid selectedId and emit a clone.
   */
  useEffect(() => {
    const firstId = entries.length ? entries[0].id : "";

    setSelectedId((prev) => (entries.some((e) => e.id === prev) ? prev : firstId));

    const selectedEntry = entries.find((e) => e.id === selectedId) || entries[0] || null;

    if (selectedEntry) {
      requestAnimationFrame(() =>
        onClone({
          ...selectedEntry,
          // ✅ ensure we send current date + status
          status,
          defaults: { ...selectedEntry.defaults, cloneDate: selectedDate },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, status]); // runs when filtering changes list

  /** Handle selecting a different neighborhood/market/school/group */
  const handleCloneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    setSelectedId(newId);

    const found = entries.find((entry) => entry.id === newId);
    if (found) {
      const updated: CloneEntry = {
        ...found,
        status, // ✅ push selected status into outgoing payload
        defaults: { ...found.defaults, cloneDate: selectedDate },
      };
      console.log("[CloneNeighborhoodControl] sending entry (clone change):", updated);
      onClone(updated);
    }
  };

  /** Handle status change */
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as StatusType;
    setStatus(next);

    // Emit updated selection (if still present after filter, effects will also handle it)
    const found = entries.find((entry) => entry.id === selectedId) || entries[0] || null;
    if (found) {
      const updated: CloneEntry = {
        ...found,
        status: next,
        defaults: { ...found.defaults, cloneDate: selectedDate },
      };
      console.log("[CloneNeighborhoodControl] sending entry (status change):", updated);
      onClone(updated);
    }
  };

  /** Handle date change (does NOT rebuild entries) */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);

    const found = entries.find((entry) => entry.id === selectedId);
    if (found) {
      const updated: CloneEntry = {
        ...found,
        status,
        defaults: { ...found.defaults, cloneDate: date },
      };
      console.log("[CloneNeighborhoodControl] sending entry (date change):", updated);
      onClone(updated);
    }
  };

  // === Theme colors ===
  const textColor = "var(--text-color)";
  const borderColor = "var(--border-color)";
  const mutedText = "var(--muted-text-color)";
  const accentColor = "var(--accent-color)";
  const dropdownBg = "var(--input-bg)";

  return (
    <div
      className={`clone-neighborhood-control theme-${theme} flex flex-col space-y-3 w-full`}
      style={{
        backgroundColor: "transparent",
        color: textColor,
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      {/* === Clone Type Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneType"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          Clone From
        </label>
        <select
          id="cloneType"
          value={cloneType}
          onChange={(e) => setCloneType(e.target.value as CloneType)}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <option value="Neighborhood">Neighborhood</option>
          <option value="Neighborhood - Group">Neighborhood - Group</option>
          <option value="Market">Market</option>
          <option value="School District">School District</option>
        </select>
      </div>

      {/* ✅ NEW: Status Selector */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneStatus"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          Status
        </label>
        <select
          id="cloneStatus"
          value={status}
          onChange={handleStatusChange}
          disabled={!statusFilterApplies}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            cursor: statusFilterApplies ? "pointer" : "not-allowed",
            opacity: statusFilterApplies ? 1 : 0.6,
          }}
        >
          <option value="Active">Active</option>
          <option value="Developing">Developing</option>
          <option value="Archived">Archived</option>
        </select>
        {!statusFilterApplies && (
          <div className="text-[11px]" style={{ color: mutedText }}>
            Status filtering only applies to Neighborhood clone types.
          </div>
        )}
      </div>

      {/* === Entity Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneSelect"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          {selectionLabel}
        </label>

        <select
          id="cloneSelect"
          value={selectedId}
          onChange={handleCloneChange}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            cursor: "pointer",
          }}
        >
          {entries.map((e) => (
            <option
              key={e.id}
              value={e.id}
              style={{
                backgroundColor: dropdownBg,
                color: textColor,
              }}
            >
              {e.title}
            </option>
          ))}
        </select>

        {statusFilterApplies && entries.length === 0 && (
          <div className="text-[11px]" style={{ color: mutedText }}>
            No entries found for status “{status}”.
          </div>
        )}
      </div>

      {/* === Date Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneDate"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          Forecast Start Date
        </label>
        <input
          id="cloneDate"
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }}
        />
      </div>

      {/* Inline theme styling */}
      <style>
        {`
          select.theme-${theme} option {
            background-color: ${dropdownBg} !important;
            color: ${textColor} !important;
          }
          select.theme-${theme}:focus option:checked {
            background-color: ${accentColor} !important;
            color: #fff !important;
          }
        `}
      </style>
    </div>
  );
};